import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconS24CrossCircle24Filled } from '@is24/cosma-ui-icons'
import { ContentStateProperty, DropdownOption, DropdownValue, LocalizedDropdownOptions } from '../../../../@types/types'
import { InputLabel } from '../../display/labelText/LabelText'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { useIsRequiredProperty } from '../../../../services/validation/requiredFieldsUtils/useIsRequiredProperty'
import {
  ButtonRounded,
  Dropdown,
  DropdownConfirmationButtonItem,
  DropdownList,
  MultiSelectDropdownItem,
  TextInput,
} from 'is24-corecss'
import {
  buildDropdownOptions,
  formHasErrors,
  getNewStyles,
  handleEmpty,
  isEqualTo,
  isNoInformation,
  isValueInvalid,
  optionToValue,
} from '../dropdowns/commonUtils'
import styles from './MultiSelectDropdown.module.css'
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll'

export type MultiSelectDropdownProps = {
  id: string
  name: ContentStateProperty
  label?: string
  labelClassName?: string
  ariaLabel?: string
  disabled?: boolean
  values?: DropdownValue[]
  noInformation?: string
  style?: React.CSSProperties
  options: DropdownOption[] | LocalizedDropdownOptions
  placeholder?: string
  allowEmpty?: boolean
  errors?: string[]
  onChange: (value: any) => void
  onBlur?: () => void
  onBlurTracking?: (value: any, isUnchanged: boolean) => void
  onFocus?: () => void
}

const notNull = (o: DropdownOption) => o.value != null

const removeNoInformation = (o: DropdownOption) => o.value !== 'NO_INFORMATION'

const valueToOption = (dropdownOptions: DropdownOption[], values: DropdownValue[] | undefined): DropdownOption[] =>
  !values
    ? dropdownOptions.filter((option) => option.value === handleEmpty(values))
    : dropdownOptions.filter((option) => values.includes(option.value))

const MultiSelectDropdown = ({
  id,
  label,
  labelClassName,
  name,
  ariaLabel,
  disabled,
  values,
  noInformation,
  style,
  options,
  placeholder,
  allowEmpty,
  errors = [],
  onChange,
  onBlur,
  onBlurTracking,
  onFocus,
}: MultiSelectDropdownProps) => {
  const { t } = useTranslation()
  const { setTouched, formState } = useFormState()
  const isFieldMandatory = useIsRequiredProperty(name)

  const dropdownOptions: DropdownOption[] = useMemo(
    () => buildDropdownOptions(isFieldMandatory, options, t, noInformation),
    [isFieldMandatory, noInformation, options, t]
  )
  const [highlightValidation, setHighlightValidation] = useState(false)
  const [isUnchanged, setIsUnchanged] = useState(true)
  const selectedOptions = useMemo(() => valueToOption(dropdownOptions, values), [dropdownOptions, values])
  const selectedOptionsRef = useRef(selectedOptions)
  const [previousOptions, setPreviousOptions] = useState(selectedOptions)
  const [currentOptions, setCurrentOptions] = useState(selectedOptions)

  const [open, setOpen] = React.useState(false)
  const handleOpen = () => setOpen((prev) => !prev)
  const handleClose = () => {
    onLostFocus()
    setOpen(false)
  }
  useDisableBodyScroll(open)

  const hasErrors = formHasErrors(errors, formState, name)
  const isInvalid = isValueInvalid(
    currentOptions.filter(removeNoInformation).map(optionToValue),
    highlightValidation,
    allowEmpty,
    hasErrors
  )
  const inputStyles = getNewStyles(style, highlightValidation, values, isInvalid)

  useEffect(() => {
    values && setHighlightValidation(true)
    if (!isEqualTo(selectedOptionsRef.current, selectedOptions)) {
      setCurrentOptions(selectedOptions)
      selectedOptionsRef.current = selectedOptions
    }
  }, [selectedOptions, values])

  const onLostFocus = () => {
    onBlur && onBlur()
    if (!isEqualTo(previousOptions, currentOptions)) {
      onBlurTracking && onBlurTracking(currentOptions, isUnchanged)
      isUnchanged && setIsUnchanged(false)
      setPreviousOptions(currentOptions)
    }
  }

  const isSelected = (item: DropdownOption) => currentOptions.map(optionToValue).includes(item.value)

  const onSelectChange = (newValue: DropdownOption[]) => {
    setHighlightValidation(true)
    onChange(newValue.map(optionToValue))
    setTouched(name)
  }

  function handleNoInformation(selectedOption: DropdownOption): DropdownOption[] {
    return [selectedOption]
  }

  function handleRegularSelect(selectedOption: DropdownOption): DropdownOption[] {
    if (!isSelected(selectedOption)) {
      return [...[...currentOptions].filter(notNull).filter(removeNoInformation), selectedOption]
    } else {
      return currentOptions.filter((o) => o.value !== selectedOption.value)
    }
  }

  const selectItem = (selectedOption: DropdownOption) => {
    let newValue

    if (isNoInformation(selectedOption)) {
      newValue = handleNoInformation(selectedOption)
    } else {
      newValue = handleRegularSelect(selectedOption)
    }

    setCurrentOptions(newValue)
    onSelectChange(newValue)
  }

  const dropdownValue = () => {
    return dropdownOptions
      .filter((o) => o.value && currentOptions.map((item) => item.value).includes(o.value))
      .map((o) => o.label)
      .join(', ')
  }

  const isVisiblePalm = `desk-hide lap-hide ${open ? '' : ' palm-hide'}`

  return (
    <>
      <div className={`${isVisiblePalm} ${styles.palmBackdrop}`} />
      <div id={`${id}-select-container`} data-testid={`${id}-select-container`} className='select-container'>
        {label && <InputLabel id={id} labelTranslationKey={label} propertyName={name} labelClass={labelClassName} />}
        <div>
          <Dropdown isOpen={open} onClose={handleClose} className='select-input-wrapper  palm-one-whole'>
            <TextInput
              id={id}
              data-testid={`${id}-select-text-input`}
              affixRight='icon'
              value={dropdownValue()}
              onClick={handleOpen}
              label={''}
              className={'select cursor-pointer font-ellipsis' + (isInvalid ? ' error' : '')}
              style={inputStyles}
              {...(disabled && { disabled: true })}
              {...(ariaLabel && { 'aria-label': ariaLabel })}
              {...(onFocus && { onFocus })}
              placeholder={placeholder ? placeholder : ''}
              readOnly={true}
            />
            <div
              data-testid={`${id}-palm-header`}
              className={`desk-hide lap-hide ${open ? '' : ' palm-hide'} ${styles.palmHeader}`}
            />
            <div className={` ${open ? '' : 'display-none'} ${styles.palmFixedContainer}`}>
              <div className={styles.palmAbsoluteContainer}>
                <DropdownList className={styles.dropdownList} isCondensed={true}>
                  {dropdownOptions
                    .filter((o) => o.value != null && o.label)
                    .map((option, index) => (
                      <MultiSelectDropdownItem
                        id={`${index}-${option.value}`}
                        key={index}
                        selected={isSelected(option)}
                        onClick={() => !disabled && selectItem(option)}
                        value={option?.value?.toString()}
                      >
                        {option.label!.toString()}
                      </MultiSelectDropdownItem>
                    ))}
                  <DropdownConfirmationButtonItem
                    onClick={handleClose}
                    label={t('wizard.screen.construction.save')}
                    className={'palm-hide'}
                  />
                </DropdownList>
              </div>
            </div>
          </Dropdown>
          <div className={`${isVisiblePalm} ${styles.palmConfirmationButton}`}>
            <ButtonRounded
              label={'Save'}
              className={'absolute-content one-whole margin-horizontal-m'}
              onClick={handleClose}
              widthMode={'expand'}
            />
          </div>
        </div>
        {isInvalid && hasErrors() && (
          <div role='alert' className='font-error' aria-labelledby={id + '-input-label'}>
            <div className='caption caption--error'>
              <IconS24CrossCircle24Filled className={'caption-icon'} />
              {t(`validation.${errors[0]}`)}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default MultiSelectDropdown
